import React from "react";
import { Link } from "react-router-dom";
import { PawPrint, MapPin, Phone, Mail, Instagram, Facebook, Navigation } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FUNDING_BANNER_URL, MAPS_DIRECTIONS, PHONE_1, PHONE_2 } from "@/i18n/translations";
import { track } from "@/lib/analytics";

export const Footer = () => {
  const { t } = useLang();

  const goSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="relative bg-brand-ink text-brand-cream" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-10 md:grid-cols-3">
        <div>
          <div className="flex items-center gap-2.5 mb-4">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand text-brand-cream">
              <PawPrint className="h-5 w-5" />
            </span>
            <span className="font-heading text-xl font-bold">Care For Me</span>
          </div>
          <p className="text-brand-cream/70 leading-relaxed max-w-xs">{t.footer.tagline}</p>
          <div className="flex gap-3 mt-5">
            <a href="https://www.instagram.com/careforme.gr/" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="grid place-items-center h-10 w-10 rounded-full bg-white/10 hover:bg-brand transition-colors duration-200">
              <Instagram className="h-5 w-5" />
            </a>
            <a href="https://www.facebook.com/profile.php?id=61588405945105&locale=el_GR" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="grid place-items-center h-10 w-10 rounded-full bg-white/10 hover:bg-brand transition-colors duration-200">
              <Facebook className="h-5 w-5" />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-heading text-lg font-bold mb-4">{t.footer.quick}</h4>
          <ul className="space-y-2.5 text-brand-cream/70">
            {["about", "services", "selfwash", "contact"].map((id) => (
              <li key={id}>
                <button onClick={() => goSection(id)} className="hover:text-brand-cream transition-colors duration-200">
                  {t.nav[id]}
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-heading text-lg font-bold mb-4">{t.footer.contact}</h4>
          <ul className="space-y-3 text-brand-cream/70">
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 mt-0.5 text-brand shrink-0" />
              <a
                href={MAPS_DIRECTIONS}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-address-link"
                onClick={() => track("directions_click", { fb_event: "Contact", location: "footer" })}
                className="hover:text-brand-cream transition-colors inline-flex items-center gap-1.5 group"
              >
                {t.contact.address}
                <Navigation className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </li>
            <li className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-brand shrink-0" />
              <span className="flex flex-wrap items-center gap-x-2">
                <a href={`tel:+30${PHONE_1}`} data-testid="footer-phone-1" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_1, location: "footer" })} className="hover:text-brand-cream transition-colors">{PHONE_1}</a>
                <span className="text-brand-cream/30">·</span>
                <a href={`tel:+30${PHONE_2}`} data-testid="footer-phone-2" onClick={() => track("call_click", { fb_event: "Contact", number: PHONE_2, location: "footer" })} className="hover:text-brand-cream transition-colors">{PHONE_2}</a>
              </span>
            </li>
            <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-brand shrink-0" /> <a href="mailto:care4me.gr@gmail.com" data-testid="footer-email" onClick={() => track("email_click", { fb_event: "Contact", location: "footer" })} className="hover:text-brand-cream transition-colors">care4me.gr@gmail.com</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-brand-cream/50">
          <p>© {new Date().getFullYear()} Care For Me. {t.footer.rights}</p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("cfm-open-cookie-banner"))}
              data-testid="footer-cookies-link"
              className="hover:text-brand-cream transition-colors"
            >
              {t.cookies.footer_link}
            </button>
            <Link to="/admin" data-testid="footer-admin-link" className="hover:text-brand-cream transition-colors">
              {t.footer.admin}
            </Link>
          </div>
        </div>
      </div>

      {/* Mandatory EU / Greece 2.0 / DYPA funding banner */}
      <div className="bg-white" data-testid="funding-banner">
        <div className="max-w-lg mx-auto px-5 sm:px-8 py-6">
          <a href="https://greece20.gov.gr/" target="_blank" rel="noreferrer" aria-label="Ελλάδα 2.0" data-testid="funding-banner-link" className="block hover:opacity-90 transition-opacity duration-200">
            <img src={FUNDING_BANNER_URL} alt="Ελλάδα 2.0 · NextGenerationEU · ΔΥΠΑ - Χρηματοδότηση" className="w-full h-auto" loading="lazy" />
          </a>
        </div>
      </div>
    </footer>
  );
};
